'use client';

import clsx from 'clsx';
import { Info, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useRef } from 'react';
import styles from './Alert.module.scss';

type AlertVariant = 'info' | 'success' | 'caution' | 'critical';

interface AlertProps {
  heading?: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6; // Set to correct heading level to match context
  children: React.ReactNode;
  variant?: AlertVariant;
  role?: 'alert' | 'status';
  autoFocus?: boolean; // Set focus to alert if dynamically added
  id: string; // Required for aria attributes
  testId?: string;
  onClose?: () => void;
}

export const Alert = ({
  heading,
  headingLevel = 2,
  children,
  variant = 'info',
  role = 'status',
  autoFocus,
  id,
  testId = id,
  onClose,
}: AlertProps) => {
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
  const t = useTranslations('components.alert');

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && alertRef.current) {
      alertRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div
      ref={alertRef}
      role={role}
      className={clsx(styles.alert, {
        [styles.alertInfo]: variant === 'info',
        [styles.alertSuccess]: variant === 'success',
        [styles.alertCaution]: variant === 'caution',
        [styles.alertCritical]: variant === 'critical',
      })}
      data-testid={testId}
      tabIndex={autoFocus ? -1 : undefined}
      aria-labelledby={heading ? `${id}-heading` : undefined}
      aria-describedby={`${id}-content`}
    >
      <Info aria-hidden="true" />
      <div className={styles.body}>
        {heading && (
          <HeadingTag className={styles.heading} id={`${id}-heading`}>
            {heading}
          </HeadingTag>
        )}
        <p className={styles.content} id={`${id}-content`}>
          {children}
        </p>
      </div>
      {onClose && (
        <button onClick={onClose} title={t('closeAlertTitle')} className={styles.closeButton}>
          <span className="sr-only">{t('closeAlert')}</span>
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
